import React from 'react';
import { Center, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { format, isValid, parse } from 'date-fns';
import { Formatter } from '../../../utils/Formatter';
import { PageableResponseWrapper } from '../../../domain/response-wrapper';
import { PaginatorFooter } from '../../../components/PaginatorFooter';
import { Currency } from '../../../domain/transaction';
import { CustomerSettlement } from '../models/customer-settlement';
import { API_DATE_PATTERN, DISPLAY_DATE_PATTERN } from '../settlements.constants';

interface SettlementsTableProps {
  results: PageableResponseWrapper<CustomerSettlement> | null;
  error: string | null;
  onNextPage: () => void;
  onPreviousPage: () => void;
  dataLoading: boolean;
}

const COLUMN_COUNT = 4;
const MISSING_VALUE = '—';
export const SETTLEMENTS_ERROR_MESSAGE = 'No pudimos cargar las liquidaciones, por favor intente de nuevo más tarde.';
export const SETTLEMENTS_EMPTY_MESSAGE = 'No se han realizado liquidaciones';

// La fecha llega como yyyy-MM-dd sin hora: se interpreta en zona local para no desfasar el día.
export const formatPaymentDate = (value: string): string => {
  const parsed = parse(value, API_DATE_PATTERN, new Date());
  return isValid(parsed) ? format(parsed, DISPLAY_DATE_PATTERN) : value;
};

const settlementRowKey = ({ settlement_id, branch_id }: CustomerSettlement): string => `${settlement_id}-${branch_id}`;

const headerStyle = { textTransform: 'none', textAlign: 'center' } as const;
const cellStyle = { fontSize: 's', textAlign: 'center' } as const;

export const SettlementsTable = ({ results, error, onPreviousPage, onNextPage, dataLoading }: SettlementsTableProps) => {
  const rows = results?.content ?? [];
  const hasError = !dataLoading && !!error;
  const isEmpty = !dataLoading && !error && !!results && rows.length === 0;
  const showRows = !dataLoading && !error;

  return (
    <>
      <TableContainer overflowY="auto"
        maxHeight={{ base: '100%', md: '25rem' }}
        minHeight="100%"
      >
        <Table variant="simple" aria-label="Liquidaciones" aria-busy={dataLoading}>
          <Thead>
            <Tr>
              <Th sx={headerStyle} scope="col">Id de liquidación</Th>
              <Th sx={headerStyle} scope="col">Fecha de pago</Th>
              <Th sx={headerStyle} scope="col">Sucursal</Th>
              <Th sx={headerStyle} scope="col">Importe neto (MXN)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {dataLoading &&
              <Tr>
                <Td colSpan={COLUMN_COUNT} paddingY="xxl">
                  <Center color="neutral.600"><Spinner /></Center>
                </Td>
              </Tr>
            }

            {hasError &&
              <Tr>
                <Td colSpan={COLUMN_COUNT} paddingY="xxl">
                  <Center color="destructive.700" role="alert">{SETTLEMENTS_ERROR_MESSAGE}</Center>
                </Td>
              </Tr>
            }

            {isEmpty &&
              <Tr>
                <Td colSpan={COLUMN_COUNT}>
                  <Text textAlign="center" color="neutral.600">{SETTLEMENTS_EMPTY_MESSAGE}</Text>
                </Td>
              </Tr>
            }

            {showRows && rows.map((settlement) => (
              <Tr key={settlementRowKey(settlement)}>
                <Td {...cellStyle}>{settlement.settlement_id}</Td>
                <Td {...cellStyle}>{formatPaymentDate(settlement.next_payment_date)}</Td>
                <Td {...cellStyle}>{settlement.branch_name ?? MISSING_VALUE}</Td>
                <Td {...cellStyle}>{Formatter.toCurrency(settlement.taxed_amount, Currency.mex)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {showRows && results &&
        <PaginatorFooter
          pageable={results.pageable}
          total_elements={results.total_elements}
          onPreviousPage={onPreviousPage}
          onNextPage={onNextPage} />
      }
    </>
  );
};
